import React, { useCallback, useEffect, useState } from 'react';
import { isEqual, toNumber } from 'lodash';
import trim from 'lodash/trim';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { setBusinessRecords } from '../../data-layer/business/actions';
import * as businessService from '../../data-layer/business/service';
import { CategoryModel } from '../../data-layer/business/types';
import { showNotificationAsync, showToastAsync } from '../../data-layer/notifications/actions';
import { useDispatch } from '../../data-layer/useDispatch';
import { setUserRecords } from '../../data-layer/user/actions';
import { Role } from '../../mappers/business/toBusinessModel';
import {
    toBusinessGeoModel,
    toBusinessPersistenceModel,
} from '../../mappers/business/toBusinessPersistenceModel';
import { toBusinessPersistenceUI } from '../../mappers/business/toBusinessPersistenceUI';
import { ImportFormat } from '../../types';
import { BusinessCategoriesUI } from '../../ui/business/BusinessCategoriesUI';
import { BusinessPersistenceForm } from '../../ui/business/BusinessPersistenceForm';
import { BusinessTypeAccountDrawer } from '../../ui/business/BusinessTypeAccountDrawer/BusinessTypeAccountDrawer';
import { GeoSuggestField } from '../../ui/business/GeoSuggestField/GeoSuggestField';
import { type BusinessPersistenceUI, type CategoryUI, BusinessType } from '../../ui/business/types';
import { DashboardPage } from '../../ui/dashboard/DashboardPage';
import { IconKebab } from '../../ui/icon/IconKebab';
import { IconButton } from '../../ui/input/IconButton';
import { Flex } from '../../ui/layout/Flex';
import { Menu, MenuItem } from '../../ui/navigation/Menu';
import { Typography } from '../../ui/typography/Typography';
import {
    canEditBusiness,
    canEditBusinessCategories,
    canEditBusinessInstagramUrl,
    canEditBusinessPublicId,
    canEditBusinessSiteUrl,
    canSeeBusinessSettings,
    isBloggerBusiness,
    isPublicIdVisible,
    isSiteVisible,
} from '../../utils/accessService';
import { addProtocol } from '../../utils/addProtocol';
import {
    trackSettingsFieldFilled,
    ymTrackGoToSupport,
    ymTrackSettingsProfileSave,
} from '../../utils/metrika';
import { useExperimentFlag } from '../../utils/useExperimentFlag';
import { useTranslation } from '../../utils/useTranslation';
import { useOpenDialog } from '../ModalRoute';
import { DIALOGS } from '../constants';
import { useCurrentUser } from '../users/useCurrentUser';
import { withBusinessAuthProtection } from '../withBusinessAuthProtection';
import { BusinessMembers } from './BusinessMembers';
import { BusinessRemoveMenuItem } from './BusinessRemoveMenuItem';
import { getUpdatedTree } from './SelectCategoriesDialog';
import { useBusinessGeo } from './hooks/useBusinessGeo';
import { useBusinessModel } from './useBusinessModel';
import { useCategories } from './useCategories';

const buildBusinessCategoriesTree = (
    category: CategoryModel,
    businessCategories: Record<number, CategoryModel>,
) => {
    const result: CategoryUI = {
        id: category.id,
        name: category.name,
        checked: Boolean(businessCategories[category.id]),
    };

    if (category.children) {
        result.children = category.children.map((childCategory) =>
            buildBusinessCategoriesTree(childCategory, businessCategories),
        );

        const checkedChildrenCount = result.children.filter(
            (childCategory) => childCategory.checked,
        ).length;

        if (checkedChildrenCount === result.children.length) {
            result.checked = true;
            result.indeterminate = false;
        } else if (checkedChildrenCount === 0) {
            result.checked = false;
            result.indeterminate = false;
        } else {
            result.checked = false;
            result.indeterminate = true;
        }
    }

    return result;
};

const getBusinessCategoriesBadges = (businessCategoriesTree: CategoryUI[]) => {
    let result: CategoryUI[] = [];

    businessCategoriesTree.forEach((categoryUI) => {
        if (categoryUI.checked && !categoryUI.children) {
            result.push(categoryUI);
        }
        if (categoryUI.children) {
            result = [...result, ...getBusinessCategoriesBadges(categoryUI.children)];
        }
    });

    return result;
};

const getDraftCategoriesList = (draftCategoriesTree: CategoryUI[]): CategoryModel[] => {
    let result: CategoryModel[] = [];

    draftCategoriesTree.forEach((categoryUI) => {
        if (categoryUI.checked && !categoryUI.children) {
            result.push({
                id: categoryUI.id,
                name: categoryUI.name,
            });
        }
        if (categoryUI.children) {
            result = [...result, ...getDraftCategoriesList(categoryUI.children)];
        }
    });

    return result;
};

const toBusinessCategoriesFromTree = (draftCategoriesTree: CategoryUI[]) => {
    let result: Record<number, CategoryUI> = {};

    draftCategoriesTree.forEach((categoryUI) => {
        if (categoryUI.checked && !categoryUI.children) {
            result[categoryUI.id] = {
                id: categoryUI.id,
                name: categoryUI.name,
            };
        }
        if (categoryUI.children) {
            result = { ...result, ...toBusinessCategoriesFromTree(categoryUI.children) };
        }
    });

    return result;
};

const normalizeValues = (ui: BusinessPersistenceUI, role?: Role): BusinessPersistenceUI => {
    const normalizedValues = {
        ...ui,
        description: trim(ui.description ?? ''),
        links: {
            ...ui.links,
            siteDisplay: ui.links?.siteDisplay ? addProtocol(ui.links?.siteDisplay) : '',
        },
        instagram: {
            ...ui.instagram,
            url: canEditBusinessInstagramUrl(role) ? ui.instagram?.url || '' : undefined,
            importFormat:
                ui.instagram?.url === '' ? ImportFormat.DO_NOT_IMPORT : ui.instagram?.importFormat,
        },
    };

    if (!canEditBusinessPublicId(role)) {
        delete normalizedValues.publicId;
    }

    return normalizedValues;
};

export const BusinessPersistence = withBusinessAuthProtection(() => {
    const dispatch = useDispatch();
    const t = useTranslation();
    const params = useParams();
    const businessId = params.businessId!;
    const business = useBusinessModel({ businessId });
    const { isLoadingCategories, categories } = useCategories();
    const [categoriesChanged, setCategoriesChanged] = useState(false);
    const currentUser = useCurrentUser();
    const navigate = useNavigate();
    const openDialog = useOpenDialog();
    const { state } = useLocation();
    const selectedCategoriesTree = state?.selectedCategoriesTree;
    const [draftBusinessCategoriesTree, setDraftBusinessCategoriesTree] = useState<CategoryUI[]>(
        [],
    );

    useEffect(() => {
        if (currentUser && business?.id && !canSeeBusinessSettings(business.id, currentUser)) {
            navigate('/', { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser, business?.id, currentUser?.profile?.id]);

    const isBlogger = isBloggerBusiness(business?.type);
    const enableBusinessGeo =
        Number(useExperimentFlag('enable_business_geo')) === 1 &&
        !isBlogger &&
        !business?.isService;
    const enableBloggerGeo = Number(useExperimentFlag('enable_blogger_geo')) === 1 && isBlogger;
    const enableGeo = enableBusinessGeo || business?.isService || enableBloggerGeo;
    const displayGeotargeting = business?.displayGeotargeting;

    const {
        selectedRegions,
        regionQuery,
        geoSuggests,
        geoChanged,
        errorMessage,
        onSelectRegion,
        onRemoveRegion,
        onInputRegion,
        updateSelectedRegions,
    } = useBusinessGeo(displayGeotargeting, { regionsLimit: 10 });

    useEffect(() => {
        if (!geoChanged && displayGeotargeting && enableGeo) {
            updateSelectedRegions(displayGeotargeting);
        }
    }, [displayGeotargeting, enableGeo, geoChanged, updateSelectedRegions]);

    const onSubmit = useCallback(
        async (
            businessUI: BusinessPersistenceUI,
            needRedirect: boolean = false,
            onSuccess?: (businessUI: BusinessPersistenceUI) => void,
        ) => {
            ymTrackSettingsProfileSave({
                page: 'Настройки бизнеса',
                userLogin: currentUser?.login,
                businessId: currentUser?.profile?.publicId,
            });

            if (!business) {
                dispatch(showNotificationAsync(t('Не удалось обновить профиль')));

                return;
            }

            try {
                const response = await businessService.updateBusiness(
                    business.id,
                    toBusinessPersistenceModel(
                        normalizeValues(
                            {
                                ...businessUI,
                                categories: toBusinessCategoriesFromTree(
                                    draftBusinessCategoriesTree,
                                ),
                                geotargeting: toBusinessGeoModel(selectedRegions),
                            },
                            currentUser?.role,
                        ),
                    ),
                );

                dispatch(setBusinessRecords(...response.records.business));

                if (needRedirect) {
                    navigate(response.records.business[0].url || business.url || '/', {
                        replace: true,
                    });
                }

                onSuccess?.(businessUI);
            } catch (_error) {
                dispatch(showNotificationAsync(t('Не удалось обновить профиль')));
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            business,
            currentUser?.login,
            currentUser?.profile?.publicId,
            navigate,
            dispatch,
            draftBusinessCategoriesTree,
            selectedRegions,
        ],
    );
    const [isOpenAccountTypeDrawer, setIsOpenAccountTypeDrawer] = useState(false);
    const onOpenAccountTypeDrawer = useCallback(() => setIsOpenAccountTypeDrawer(true), []);
    const onCloseAccountTypeDrawer = useCallback(() => setIsOpenAccountTypeDrawer(false), []);
    const onSuccessChangeAccountType = useCallback(
        (businessUI: BusinessPersistenceUI) => {
            if (!business || !currentUser?.profile) {
                return;
            }

            dispatch(
                setUserRecords({
                    ...currentUser,
                    profile: {
                        ...currentUser.profile,
                        isService: businessUI.isService,
                    },
                }),
            );

            dispatch(
                showToastAsync({
                    message: t('Тип аккаунта изменён'),
                    color: 'invert',
                    minWidth: { xs: '100%', sm: '400px' },
                }),
            );
        },
        [business, dispatch, currentUser, t],
    );
    const onChangeAccountType = useCallback(
        (value: BusinessType) => {
            if (!business) {
                return;
            }

            onSubmit(
                {
                    ...toBusinessPersistenceUI(business),
                    isService: value === BusinessType.SERVICE,
                },
                false,
                onSuccessChangeAccountType,
            );
        },
        [business, onSubmit, onSuccessChangeAccountType],
    );

    const onSupportClick = useCallback(() => {
        ymTrackGoToSupport({
            page: 'Настройки бизнеса',
            userLogin: currentUser?.login,
            businessId: currentUser?.profile?.publicId,
        });
    }, [currentUser?.login, currentUser?.profile?.publicId]);

    const onFieldBlur = useCallback(
        (fieldName: string, value: string) => {
            if (value) {
                trackSettingsFieldFilled(fieldName, {
                    page: 'Настройки бизнеса',
                    userLogin: currentUser?.login,
                    businessId: currentUser?.profile?.publicId,
                    value,
                });
            }
        },
        [currentUser?.login, currentUser?.profile?.publicId],
    );

    useEffect(() => {
        if (business?.categories && categories) {
            const businessCategoriesTree = categories.map((category) =>
                buildBusinessCategoriesTree(category, business.categories || {}),
            );

            setDraftBusinessCategoriesTree(businessCategoriesTree);
        }
    }, [business?.categories, categories]);

    useEffect(() => {
        if (selectedCategoriesTree) {
            setDraftBusinessCategoriesTree(selectedCategoriesTree);
        }
    }, [selectedCategoriesTree]);

    const onRemoveBusinessCategory = useCallback(
        (categoryId: number) => {
            const newDraft = getUpdatedTree(categoryId, false, draftBusinessCategoriesTree) || [];
            setDraftBusinessCategoriesTree(newDraft);
        },
        [draftBusinessCategoriesTree],
    );

    const onOpenSelectCategoriesDialog = useCallback(() => {
        openDialog(DIALOGS.SELECT_CATEGORIES, {
            draftBusinessCategoriesTree,
            saveLink: business?.editLink,
        });
    }, [openDialog, draftBusinessCategoriesTree, business?.editLink]);

    useEffect(() => {
        const initialCategoriesIds = business?.categories
            ? Object.keys(business.categories).map(toNumber).sort()
            : [];
        const draftCategoriesIds = getDraftCategoriesList(draftBusinessCategoriesTree)
            .map((categoryModel) => categoryModel.id)
            .sort();

        setCategoriesChanged(!isEqual(initialCategoriesIds, draftCategoriesIds));
    }, [business?.categories, draftBusinessCategoriesTree, setCategoriesChanged]);

    if (!business) {
        return null;
    }

    return (
        <DashboardPage>
            <Flex
                width="100%"
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                py={1}
                px={2}
                mb={1}
            >
                <Typography variant="title2" weight="bold">
                    {t('Настройки профиля')}
                </Typography>
                <Menu
                    trigger={
                        <IconButton size="m">
                            <IconKebab />
                        </IconButton>
                    }
                >
                    {isBlogger ? undefined : (
                        <MenuItem onClick={onOpenAccountTypeDrawer}>
                            <Typography noWrap>{t('Сменить тип аккаунта')}</Typography>
                        </MenuItem>
                    )}
                    <BusinessRemoveMenuItem business={business} user={currentUser} />
                </Menu>
            </Flex>
            <BusinessPersistenceForm
                initialValues={toBusinessPersistenceUI(business)}
                canEditPublicId={canEditBusinessPublicId(currentUser?.role)}
                canEditInstagramUrl={canEditBusinessInstagramUrl(currentUser?.role)}
                canEditSiteUrl={canEditBusinessSiteUrl(currentUser?.role)}
                canEditBusiness={canEditBusiness(businessId, currentUser)}
                canEditBusinessCategories={canEditBusinessCategories(currentUser?.role)}
                canEditProducts={business.showProductsImportSettings ? !business.isService : false}
                categoriesChanged={categoriesChanged}
                isPublicIdVisible={isPublicIdVisible(business.type)}
                isSiteVisible={isSiteVisible(business.type)}
                needChangeNameInPassport={isBloggerBusiness(business.type)}
                businessType={business.type}
                login={currentUser?.login}
                publicId={business.publicId}
                onSubmit={onSubmit}
                membersSlot={
                    business && !isBloggerBusiness(business.type) ? (
                        <BusinessMembers businessId={businessId} />
                    ) : undefined
                }
                categoriesSlot={
                    <BusinessCategoriesUI
                        isLoading={isLoadingCategories}
                        businessCategories={getBusinessCategoriesBadges(
                            draftBusinessCategoriesTree,
                        )}
                        onRemove={onRemoveBusinessCategory}
                        onOpenSelectCategoriesDialog={onOpenSelectCategoriesDialog}
                    />
                }
                geoSlot={
                    enableGeo ? (
                        <GeoSuggestField
                            selected={selectedRegions}
                            suggests={geoSuggests}
                            value={regionQuery}
                            errorMessage={errorMessage}
                            onSelect={onSelectRegion}
                            onRemove={onRemoveRegion}
                            onInput={onInputRegion}
                            required
                            regionsLimit={10}
                        />
                    ) : undefined
                }
                geoChanged={geoChanged}
                selectedRegions={selectedRegions}
                onSupportClick={onSupportClick}
                onFieldBlur={onFieldBlur}
            />
            {isOpenAccountTypeDrawer
                ? createPortal(
                      <BusinessTypeAccountDrawer
                          type={business.isService ? BusinessType.SERVICE : BusinessType.BUSINESS}
                          onChange={onChangeAccountType}
                          onClose={onCloseAccountTypeDrawer}
                      />,
                      document.body,
                  )
                : null}
        </DashboardPage>
    );
});
